import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, of, switchMap, withLatestFrom } from 'rxjs';

import { FrodoService } from 'src/app/shared/services';
import { DEFAULT_SETTINGS } from 'src/app/shared/services/appconfig/default-settings';
import { TSettings } from 'src/app/shared/services/appconfig/settings.model';
import { errorToJSON } from 'src/app/shared/utils';
import { deepMerge } from 'src/app/shared/utils';

import { LOCAL_STORAGE_STORE_KEY } from '../storage.metareducer';

import * as actions from './settings.actions';
import { selectSettings } from './settings.selector';

export const getCachedSettings = (): Partial<TSettings> => {
  const savedState = getLocalStorageState();
  return savedState && savedState.settings
    ? savedState.settings
    : (DEFAULT_SETTINGS as Partial<TSettings>);
};

const getLocalStorageState = (): { settings: Partial<TSettings> } | null => {
  try {
    const data = localStorage.getItem(LOCAL_STORAGE_STORE_KEY);
    return data ? JSON.parse(data).settings : null;
  } catch (e) {
    return null;
  }
};

@Injectable()
export class SettingsEffects {
  constructor(
    private actions$: Actions,
    private sanitizer: DomSanitizer,
    private store: Store,
    private frodoApiService: FrodoService
  ) {}

  loadSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadSettings),
      withLatestFrom(this.store.select(selectSettings)),
      switchMap(([_action, settingsInStore]) => {
        return this.frodoApiService.getSettings().pipe(
          map((settings: TSettings) => {
            const cachedSettings = getCachedSettings() || settingsInStore;
            const normalizedSettings = this.normalizeSettings(cachedSettings, settings);
            this.setSettingsToLocalStorage(normalizedSettings);
            return actions.loadSettingsSuccess({
              settings: normalizedSettings,
            });
          }),
          catchError((error) => {
            return of(actions.loadSettingsFailure({ error: errorToJSON(error) }));
          })
        );
      })
    )
  );

  updateSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.updateSettings),
      switchMap(({ settings }) => {
        const cachedSettings = this.getCachedSettings();
        const normalizedSettings = this.normalizeSettings(cachedSettings, settings);
        this.setSettingsToLocalStorage(normalizedSettings);
        return of(
          actions.updateSettingsSuccess({
            settings: normalizedSettings,
          })
        );
      }),
      catchError((error) => {
        return of(actions.updateSettingsFailure({ error }));
      })
    )
  );

  getCachedSettings(): Partial<TSettings> {
    return getCachedSettings();
  }

  setSettingsToLocalStorage(settings: Partial<TSettings>): void {
    localStorage.setItem(LOCAL_STORAGE_STORE_KEY, JSON.stringify({ settings }));
  }

  normalizeSettings(
    previousSettings: Partial<TSettings>,
    nextSettings: Partial<TSettings>
  ): TSettings {
    const settings = deepMerge(previousSettings, nextSettings);

    const { logo, icons } = settings.apps.frodo.style;
    const { order, phone, scan } = icons;

    const sanitizedImages = [logo, order, phone, scan].map((image) => {
      if (!image.startsWith('.')) {
        return this.sanitizer.bypassSecurityTrustResourceUrl(image);
      }
      return image;
    });

    settings.apps.frodo.style.logo = sanitizedImages[0];
    settings.apps.frodo.style.icons.order = sanitizedImages[1];
    settings.apps.frodo.style.icons.phone = sanitizedImages[2];
    settings.apps.frodo.style.icons.scan = sanitizedImages[3];

    return settings;
  }
}
